import { ClaimMessage, ClaimMessageContact } from '@/api/claims'
import { Typography } from '@/components'
import { Collapse } from '@/components/molecules/Collapse/Component'
import { ThemeColor } from '@/enums'
import { ddMMyyyy } from '@/utils'
import dayjs from 'dayjs'
import { ArrowLeft, ArrowRight, Download, Mail, Paperclip } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  ContactLine,
  MessageAttachmentDownload,
  MessageAttachmentRow,
  MessageAttachments,
  MessageBody,
  MessageCard,
  MessageCardHeader,
  MessageDateColumn,
  MessageIcons,
  MessageMeta,
  MessageMetaRow,
  MessageTitleColumn,
  ReplyButton,
  UnreadDot,
} from './styles'

type MessageItemProps = {
  message: ClaimMessage
  isOpen: boolean
  onToggle: () => void
  onReply: () => void
  onDownloadAttachment: (attachmentId: string) => void
}

const formatContacts = (contacts: ClaimMessageContact[]) =>
  contacts.map((contact) => contact.name || contact.email).join(', ')

const MessageItem = ({
  message,
  isOpen,
  onToggle,
  onReply,
  onDownloadAttachment,
}: MessageItemProps) => {
  const { t } = useTranslation()

  const isOutbound = message.direction === 'outbound'
  const counterpart = isOutbound
    ? formatContacts(message.to) || '--'
    : message.from
    ? formatContacts([message.from])
    : '--'

  return (
    <MessageCard $isOpen={isOpen}>
      <Collapse
        isOpen={isOpen}
        onCollapse={onToggle}
        divide
        showIcon={false}
        title={
          <MessageTitleColumn>
            <MessageCardHeader>
              <ContactLine>
                {message.is_unread && <UnreadDot />}
                <MessageIcons>
                  <Mail size={15} />
                  {isOutbound ? (
                    <ArrowLeft size={13} />
                  ) : (
                    <ArrowRight size={13} />
                  )}
                </MessageIcons>
                <Typography variant="p2Body" ellipsis>
                  {counterpart}
                </Typography>
              </ContactLine>
              <MessageDateColumn>
                <Typography color={ThemeColor.b50} variant="captionR">
                  {ddMMyyyy(message.sent_at)}{' '}
                  {dayjs(message.sent_at).format('HH:mm')}
                </Typography>
                <ReplyButton
                  onClick={(e) => {
                    e.stopPropagation()
                    onReply()
                  }}
                >
                  {t('claimsPage.detail.messages.reply')}
                </ReplyButton>
              </MessageDateColumn>
            </MessageCardHeader>
            <Typography bold={message.is_unread} variant="p2Body" ellipsis>
              {message.subject || t('claimsPage.detail.messages.noSubject')}
            </Typography>
          </MessageTitleColumn>
        }
      >
        <MessageMeta>
          <MessageMetaRow>
            <Typography color={ThemeColor.b50} variant="p2Body">
              {t('claimsPage.detail.messages.from')}
            </Typography>
            <Typography variant="p2Body">
              {message.from ? formatContacts([message.from]) : '--'}
            </Typography>
          </MessageMetaRow>
          <MessageMetaRow>
            <Typography color={ThemeColor.b50} variant="p2Body">
              {t('claimsPage.detail.messages.to')}
            </Typography>
            <Typography variant="p2Body">
              {formatContacts(message.to) || '--'}
            </Typography>
          </MessageMetaRow>
        </MessageMeta>
        {/* body is pre-sanitized server-side, safe to render as-is */}
        <MessageBody dangerouslySetInnerHTML={{ __html: message.body }} />
        {message.attachments.length > 0 && (
          <MessageAttachments>
            {message.attachments.map((attachment) => (
              <MessageAttachmentRow key={attachment.id}>
                <Paperclip size={14} />
                <Typography variant="p2Body" ellipsis>
                  {attachment.filename}
                </Typography>
                <MessageAttachmentDownload
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownloadAttachment(attachment.id)
                  }}
                  aria-label={t(
                    'claimsPage.detail.messages.downloadAttachment',
                  )}
                >
                  <Download size={14} />
                </MessageAttachmentDownload>
              </MessageAttachmentRow>
            ))}
          </MessageAttachments>
        )}
      </Collapse>
    </MessageCard>
  )
}

export default MessageItem
